import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AnimateHeight, { Height } from 'react-animate-height';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import {
  AmlHistoryRequest,
  AmlStatusEnum,
  ExportHistoryResponse,
  amlbotAPI,
} from '@/api';
import { AmlHistoryItemData } from '@/redux/models';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  amlCheckActions,
  amlHistoryActions,
  userActions,
} from '@/redux/actions';
import {
  amlHistorySelectors,
  profileSelectors,
  userSelectors,
} from '@/redux/selectors';
import {
  castDateFilters,
  downloadCsv,
  getFormattedDate,
  getSignalTranslation,
} from '@/helpers';
import { useApiCall } from '@/hooks';
import { toast } from 'react-toastify';
import { COMMON_CONTAINER_ID } from '@/config';
import { capitalize } from 'lodash';

import {
  AlertBox,
  Button,
  HistoryEmpty,
  Loader,
  SectionBox,
} from '@/components';
import { HistoryAmlTable, TableTools } from './components';

import styles from './RequestHistory.module.sass';

const STORE_DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Note: `createTo` should add 1 day because of backend filtering logic
 */
const getFilledFilters = (filterList: Record<string, any>) => {
  const filters = {
    ...(!!filterList.asset ? { asset: filterList.asset } : {}),
    ...(!!filterList.status ? { status: filterList.status } : {}),
    ...(!!filterList.type ? { type: filterList.type } : {}),
    ...(!!filterList.createFrom
      ? { createFrom: getFormattedDate(filterList.createFrom, 'YYYY-MM-DD') }
      : {}),
    ...(!!filterList.createTo
      ? {
          createTo: getFormattedDate(
            filterList.createTo.add(1, 'days'),
            'YYYY-MM-DD',
          ),
        }
      : {}),
  };

  return Object.keys(filters).length ? { filter: filters } : {};
};

interface RequestHistoryProps {}

const RequestHistory: FC<RequestHistoryProps> = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loaderHeight, setLoaderHeight] = useState<Height>(0);
  const theInterval = useRef<any>(null);

  const { loading, error, data, filters, queryHash } = useAppSelector(
    amlHistorySelectors.getAmlHistorySlice,
  );
  const searchFilters = useMemo(() => castDateFilters(filters), [filters]);
  const isEmailConfirmed = useAppSelector(userSelectors.checkIfEmailConfirmed);
  const canDownloadPDFReport = useAppSelector(
    profileSelectors.canDownloadPDFReport,
  );
  const amlDetailsFor = useAppSelector(userSelectors.amlDetailsFor);

  useEffect(() => {
    // fetch data on the first entrance, honouring filters restored from the store
    if (!data) {
      dispatch(
        amlHistoryActions.fetchAmlHistory({
          queryHash,
          ...getFilledFilters(searchFilters),
        }),
      );
    }
  }, [data, dispatch]);

  useEffect(() => {
    const shouldRefetch =
      !!data &&
      data.items
        .slice(0, 15)
        .some((item) => item.status === AmlStatusEnum.PENDING);

    if (shouldRefetch) {
      theInterval.current = setInterval(() => {
        const payload: AmlHistoryRequest = {
          queryHash,
          ...getFilledFilters(searchFilters),
        };

        dispatch(amlHistoryActions.fetchAmlHistory(payload));
      }, 15000);
    } else {
      clearInterval(theInterval.current);
      theInterval.current = null;
    }

    return () => {
      clearInterval(theInterval.current);
    };
  }, [data, dispatch]);

  useEffect(() => {
    setLoaderHeight(loading ? 'auto' : 0);
  }, [loading]);

  const checkHandler = (item: AmlHistoryItemData) => {
    dispatch(amlCheckActions.checkAgain(item));
  };

  const detailsHandler = useCallback(
    (item: AmlHistoryItemData) => {
      // filters no longer travel through the router state - they live in the store
      navigate('/details', { state: { item } });
    },
    [navigate],
  );

  useEffect(() => {
    if (amlDetailsFor && !!data?.items.length) {
      const item = data?.items.find((item) => item.uuid === amlDetailsFor);
      if (item) {
        detailsHandler(item);
        dispatch(userActions.discardAmlDetailsFor());
      }
    }
  }, [dispatch, amlDetailsFor, data?.items, detailsHandler]);

  const showMoreHandler = useCallback(() => {
    if (data) {
      const itemsToLoadTotal = data.totalCount - data.items.length;

      if (itemsToLoadTotal > 0) {
        const currentPage = Math.ceil(data.items.length / data.pageLimit);

        const payload: AmlHistoryRequest = {
          // backend counts pages from 0
          page: currentPage,
          queryHash,
          ...getFilledFilters(searchFilters),
        };

        dispatch(amlHistoryActions.fetchAmlHistory(payload));
      }
    }
  }, [dispatch, data, queryHash, searchFilters]);

  const searchHandler = useCallback(
    (filtersOverride: Record<string, any> | null = null, term?: string) => {
      const payload: AmlHistoryRequest = {
        queryHash: term ?? queryHash,
        ...getFilledFilters(filtersOverride || searchFilters),
      };

      dispatch(amlHistoryActions.fetchAmlHistory(payload));
    },
    [dispatch, queryHash, searchFilters],
  );

  const queryChangeHandler = useCallback(
    (newValue: string) => {
      dispatch(amlHistoryActions.setQueryHash(newValue));

      if (newValue.length === 0) {
        searchHandler(searchFilters, '');
      }
    },
    [dispatch, searchHandler, searchFilters],
  );

  const [downloadHistory, downloadLoading, downloadError, downloadErrorCode] =
    useApiCall(async () => {
      const payload: AmlHistoryRequest = {
        queryHash,
        ...getFilledFilters(searchFilters),
      };
      const result: ExportHistoryResponse =
        await amlbotAPI.exportHistory(payload);

      if (result.success) {
        const {
          data: { data: rows },
        } = result;

        if (!rows.length) {
          toast.error('Nothing to export', {
            containerId: COMMON_CONTAINER_ID,
          });
          return;
        }

        const filename = `check_results`;
        const headerRow = rows[0].map((val: string) =>
          capitalize(getSignalTranslation(val)),
        );

        rows[0] = headerRow;
        downloadCsv(rows, filename);
      } else {
        toast.error(downloadError, {
          containerId: COMMON_CONTAINER_ID,
        });
      }
    });

  const filtersChanged = (key: string, value: any) => {
    dispatch(
      amlHistoryActions.setFilters({
        ...filters,
        [key]: dayjs.isDayjs(value) ? value.format(STORE_DATE_FORMAT) : value,
      }),
    );
  };

  const onFilterApply = () => {
    searchHandler();
  };

  const onFilterClear = () => {
    dispatch(amlHistoryActions.resetFilters());
    searchHandler({});
  };

  const onDownload = () => {
    downloadHistory();
  };

  const isEmpty = !data || data.items.length === 0;
  const isShowMoreAvailable = !!data && data.totalCount - data.items.length > 0;

  const emptyHistoryTitle = t('translation.empty_aml_history_title');
  const emptyHistoryDesc = t('translation.empty_aml_history_description');
  const sectionTitle = t('translation.request_history');

  return (
    <SectionBox>
      {{
        headerClassName: styles.sectionHeader,
        title: sectionTitle,
        actions: isEmailConfirmed && (
          <TableTools
            loading={loading}
            onSearchChangeHandler={(newVal) => queryChangeHandler(newVal)}
            onSearchHandler={searchHandler}
            term={queryHash}
            filters={searchFilters}
            onFiltersChangeHandler={filtersChanged}
            onApplyHandler={onFilterApply}
            onClearHandler={onFilterClear}
            onDownloadHandler={onDownload}
          />
        ),
        body: (
          <>
            {!!error && (
              <div className={styles.error}>
                <AlertBox type="danger">
                  {{
                    title: t('translation.ooops'),
                    description: error,
                  }}
                </AlertBox>
              </div>
            )}

            <AnimateHeight
              className={styles.loader}
              duration={100}
              height={loaderHeight}
            >
              <Loader />
            </AnimateHeight>

            {isEmpty && (
              <HistoryEmpty title={emptyHistoryTitle} desc={emptyHistoryDesc} />
            )}

            {!isEmpty && (
              <div>
                <HistoryAmlTable
                  canDownloadPDFReport={canDownloadPDFReport}
                  data={data.items}
                  onCheck={checkHandler}
                  onDetails={detailsHandler}
                  loading={loading}
                />

                {isShowMoreAvailable && (
                  <div className={styles.show}>
                    <Button
                      size="m"
                      onClick={showMoreHandler}
                      disabled={loading}
                      nowrap
                    >
                      {t('translation.show_more')}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </>
        ),
      }}
    </SectionBox>
  );
};

export default RequestHistory;
